import type { ConnectionStatus } from '../../../core/domain/controller';

/*
 * When a failed-apply message on an integration screen has gone stale.
 *
 * A re-apply against an unreachable backend leaves prose on screen — "saved and
 * is connecting … it will keep retrying automatically in the background". That
 * sentence is true when it is written and becomes false the moment the retry
 * loop succeeds, because the loop recovers WITHOUT another click: nothing in the
 * component's own flow runs again to clear it. So the screen kept telling the
 * operator the integration was still trying while it was connected and serving.
 *
 * The rule is a TRANSITION, not a state. An error raised while the integration
 * is connected and stays connected has nothing to do with reachability — a
 * refused target, a permission denial, a malformed operation — and must survive.
 * Only an integration that was NOT connected and now is has actually answered
 * the question the message was asking.
 *
 * Pure so the distinction is pinned in CI rather than only findable by pulling a
 * mock server out from under a running screen.
 */

/** Whether a status change from `prev` to `next` retires a pending apply error.
 *  `prev` is undefined before the first status has been observed. */
export function statusClearsApplyError(prev: ConnectionStatus | undefined, next: ConnectionStatus | undefined): boolean {
  // Nothing observed yet: the first status is not a recovery, it is the baseline.
  if (prev === undefined) return false;
  return next === 'connected' && prev !== 'connected';
}
